import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, CheckCircle, XCircle, FileText, ExternalLink, User, Calendar, Hash } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const CHECK_NAMES = {
  'address_check': 'Address Verification',
  'criminal_court_check': 'Criminal Court Record',
  'id_check': 'ID Verification (PAN/Aadhar)',
  'employment_check': 'Employment Verification',
  'education_check': 'Education Verification',
  'credit_check': 'Credit Check',
  'personal_reference': 'Personal Reference',
  'professional_reference': 'Professional Reference',
  'world_criminal_db': 'World Database Check',
  'india_criminal_db': 'India Criminal Database',
  'drug_test': 'Drug Test'
};

export default function CheckDetailsView({ caseId, checkId, onBack, onUpdate }) {
  const [loading, setLoading] = useState(true);
  const [checkDetails, setCheckDetails] = useState(null);
  const [showInsuffDialog, setShowInsuffDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [agents, setAgents] = useState([]);
  const [insuffForm, setInsuffForm] = useState({ reason: '', description: '' });
  const [approveForm, setApproveForm] = useState({ assignTo: '', bypassInsuff: false });

  useEffect(() => {
    loadCheckDetails();
    loadAgents();
  }, [caseId, checkId]);

  const loadCheckDetails = async () => {
    try {
      const response = await axios.get(`/cases/${caseId}/checks/${checkId}/details`);
      setCheckDetails(response.data);
    } catch (error) {
      toast.error('Failed to load check details');
    } finally {
      setLoading(false);
    }
  };

  const loadAgents = async () => {
    try {
      const response = await axios.get('/users?role=verification_team');
      setAgents(response.data || []);
    } catch (error) {
      console.log('Could not load agents');
    }
  };

  const handleRaiseInsufficiency = async () => {
    if (!insuffForm.reason || !insuffForm.description) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await axios.post(`/cases/${caseId}/checks/${checkId}/insufficiency?reason=${encodeURIComponent(insuffForm.reason)}&description=${encodeURIComponent(insuffForm.description)}`);
      toast.success('Insufficiency raised successfully');
      setShowInsuffDialog(false);
      setInsuffForm({ reason: '', description: '' });
      loadCheckDetails();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to raise insufficiency');
    }
  };

  const handleClearInsufficiency = async () => {
    try {
      await axios.put(`/cases/${caseId}/checks/${checkId}/clear-insufficiency`);
      toast.success('Insufficiency cleared');
      loadCheckDetails();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error('Failed to clear insufficiency');
    }
  };

  const handleApproveCheck = async () => {
    try {
      let url = `/cases/${caseId}/checks/${checkId}/approve?bypass_insufficiency=${approveForm.bypassInsuff}`;
      if (approveForm.assignTo) {
        url += `&assign_to=${approveForm.assignTo}`;
      }
      await axios.put(url);
      toast.success('Check approved successfully');
      setShowApproveDialog(false);
      loadCheckDetails();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve check');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      'pending': 'bg-amber-100 text-amber-700',
      'in_progress': 'bg-blue-100 text-blue-700',
      'verified': 'bg-emerald-100 text-emerald-700',
      'failed': 'bg-rose-100 text-rose-700'
    };
    return styles[status] || styles['pending'];
  };

  if (loading) {
    return <div className="p-8 text-center">Loading check details...</div>;
  }

  if (!checkDetails) {
    return <div className="p-8 text-center text-slate-500">Check not found</div>;
  }

  const { check, employee, documents, insufficiencies, auto_detected_issues } = checkDetails;
  const openInsuffs = insufficiencies?.filter(i => i.status === 'open') || [];
  const hasIssues = auto_detected_issues?.length > 0 || openInsuffs.length > 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack}>Back to List</Button>
          <h2 className="text-xl font-bold">{CHECK_NAMES[check.check_type] || check.check_type}</h2>
          <Badge className={getStatusBadge(check.status)}>{check.status?.toUpperCase()}</Badge>
          {check.has_insufficiency && <Badge className="bg-rose-100 text-rose-700">Has Insufficiency</Badge>}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="border-rose-300 text-rose-600" onClick={() => setShowInsuffDialog(true)}>
            <AlertTriangle className="w-4 h-4 mr-2" />Raise Insufficiency
          </Button>
          {openInsuffs.length > 0 && (
            <Button variant="outline" onClick={handleClearInsufficiency}>
              <XCircle className="w-4 h-4 mr-2" />Clear Insufficiency
            </Button>
          )}
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setShowApproveDialog(true)}>
            <CheckCircle className="w-4 h-4 mr-2" />Approve & Assign
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Hash className="w-5 h-5" />Check Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium">Check ID:</span> {check.check_id}</div>
              <div><span className="font-medium">Status:</span> {check.status}</div>
              <div><span className="font-medium">Case ID:</span> {caseId}</div>
              <div><span className="font-medium">Date Added:</span> {checkDetails.case_created_at ? new Date(checkDetails.case_created_at).toLocaleDateString() : 'N/A'}</div>
              {check.qc_approved && <div className="col-span-2"><span className="font-medium">QC Approved:</span> {new Date(check.qc_approved_at).toLocaleString()}</div>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />Candidate
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1">
            <p><span className="font-medium">Name:</span> {employee?.name || 'N/A'}</p>
            <p><span className="font-medium">Email:</span> {employee?.email || 'N/A'}</p>
            <p><span className="font-medium">Phone:</span> {employee?.phone || 'N/A'}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Candidate Submitted Data</CardTitle>
        </CardHeader>
        <CardContent>
          {check.check_type === 'id_check' && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium">PAN Number:</span> {employee?.pan_number || <span className="text-rose-500">Not Provided</span>}</div>
              <div><span className="font-medium">Aadhar Number:</span> {employee?.aadhar_number || <span className="text-rose-500">Not Provided</span>}</div>
              <div><span className="font-medium">DL Number:</span> {employee?.dl_number || 'N/A'}</div>
              <div><span className="font-medium">Passport:</span> {employee?.passport_number || 'N/A'}</div>
            </div>
          )}
          {check.check_type === 'address_check' && (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="col-span-2">
                <span className="font-medium">Present Address:</span><br />
                {employee?.present_house_no ? `${employee.present_house_no}, ${employee.present_area || ''}, ${employee.present_city || ''}, ${employee.present_state || ''} - ${employee.present_pincode || ''}` : <span className="text-rose-500">Not Provided</span>}
              </div>
              <div><span className="font-medium">Residence Type:</span> {employee?.present_residence_type || 'N/A'}</div>
              <div><span className="font-medium">Period of Stay:</span> {employee?.present_period_of_stay || 'N/A'}</div>
            </div>
          )}
          {check.check_type === 'employment_check' && (
            <div className="text-sm space-y-2">
              {employee?.is_fresher ? (
                <p className="text-amber-600">Candidate marked as Fresher</p>
              ) : employee?.latest_employment ? (
                <div className="grid grid-cols-2 gap-4">
                  <div><span className="font-medium">Employer:</span> {employee.latest_employment.employer_name || 'N/A'}</div>
                  <div><span className="font-medium">Designation:</span> {employee.latest_employment.designation || 'N/A'}</div>
                  <div><span className="font-medium">Employee Code:</span> {employee.latest_employment.employee_code || 'N/A'}</div>
                  <div><span className="font-medium">Joining Date:</span> {employee.latest_employment.date_of_joining || 'N/A'}</div>
                  <div><span className="font-medium">Relieving Date:</span> {employee.latest_employment.date_of_relieving || 'N/A'}</div>
                  <div><span className="font-medium">HR Contact:</span> {employee.latest_employment.hr_email || 'N/A'}</div>
                  {employee.latest_employment.relieving_reason && (
                    <div className="col-span-2 p-2 bg-amber-50 rounded">
                      <span className="font-medium text-amber-700">Reason for missing relieving letter:</span> {employee.latest_employment.relieving_reason}
                    </div>
                  )}
                </div>
              ) : <span className="text-rose-500">Employment details not provided</span>}
            </div>
          )}
          {check.check_type === 'education_check' && (
            <div className="text-sm">
              {employee?.highest_education ? (
                <div className="grid grid-cols-2 gap-4">
                  <div><span className="font-medium">University:</span> {employee.highest_education.university || 'N/A'}</div>
                  <div><span className="font-medium">Institution:</span> {employee.highest_education.institution_name || 'N/A'}</div>
                  <div><span className="font-medium">Course:</span> {employee.highest_education.course || 'N/A'}</div>
                  <div><span className="font-medium">Year:</span> {employee.highest_education.year_of_passing || 'N/A'}</div>
                  <div><span className="font-medium">Specialization:</span> {employee.highest_education.specialization || 'N/A'}</div>
                  <div><span className="font-medium">Grade/Class:</span> {employee.highest_education.class_degree || 'N/A'}</div>
                </div>
              ) : <span className="text-rose-500">Education details not provided</span>}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />Documents ({documents?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {documents?.length > 0 ? (
            <div className="space-y-2">
              {documents.map((doc, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium">{doc.document_type?.replace(/_/g, ' ').toUpperCase()}</p>
                    <p className="text-sm text-slate-500">{doc.file_name}</p>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => window.open(doc.file_url, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-2" />View
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500">No documents uploaded for this check</p>
          )}
        </CardContent>
      </Card>

      {hasIssues && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-5 h-5" />Detected Issues & Insufficiencies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {auto_detected_issues?.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Auto-Detected Issues:</h4>
                {auto_detected_issues.map((issue, idx) => (
                  <div key={idx} className="p-2 bg-white rounded mb-2">
                    <p className="text-amber-700">{issue.message}</p>
                    {issue.candidate_reason && (
                      <p className="text-sm text-slate-600 mt-1">Candidate Reason: {issue.candidate_reason}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
            {openInsuffs.length > 0 && (
              <div>
                <h4 className="font-medium mb-2">Open Insufficiencies:</h4>
                {openInsuffs.map((insuff, idx) => (
                  <div key={idx} className="p-3 bg-white rounded mb-2 border-l-4 border-rose-400">
                    <p className="font-medium text-rose-700">{insuff.reason}</p>
                    <p className="text-sm">{insuff.description}</p>
                    <p className="text-xs text-slate-500 mt-1">Raised: {new Date(insuff.created_at).toLocaleString()}</p>
                    {insuff.candidate_response && (
                      <div className="mt-2 p-2 bg-blue-50 rounded">
                        <p className="text-sm"><span className="font-medium">Candidate Response:</span> {insuff.candidate_response}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={showInsuffDialog} onOpenChange={setShowInsuffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Raise Insufficiency</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Reason</Label>
              <Select value={insuffForm.reason} onValueChange={(v) => setInsuffForm({...insuffForm, reason: v})}>
                <SelectTrigger><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="missing_document">Missing Document</SelectItem>
                  <SelectItem value="unclear_document">Unclear/Illegible Document</SelectItem>
                  <SelectItem value="incorrect_data">Incorrect Data</SelectItem>
                  <SelectItem value="incomplete_data">Incomplete Data</SelectItem>
                  <SelectItem value="mismatch">Data Mismatch</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                placeholder="Describe the insufficiency in detail..."
                value={insuffForm.description}
                onChange={(e) => setInsuffForm({...insuffForm, description: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInsuffDialog(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={handleRaiseInsufficiency}>Raise Insufficiency</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Check & Assign Agent</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {openInsuffs.length > 0 && (
              <div className="p-3 bg-amber-50 rounded-lg">
                <p className="text-amber-700 font-medium">Warning: {openInsuffs.length} open insufficiency(ies)</p>
                <label className="flex items-center gap-2 mt-2">
                  <input 
                    type="checkbox" 
                    checked={approveForm.bypassInsuff}
                    onChange={(e) => setApproveForm({...approveForm, bypassInsuff: e.target.checked})}
                  />
                  <span className="text-sm">Bypass insufficiency and approve anyway</span>
                </label>
              </div>
            )}
            <div>
              <Label>Assign to Agent (Optional)</Label>
              <Select value={approveForm.assignTo} onValueChange={(v) => setApproveForm({...approveForm, assignTo: v})}>
                <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                <SelectContent>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApproveCheck}>
              {approveForm.bypassInsuff ? 'Bypass & Approve' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
